import { Page } from 'web-mojo';
import config from '../../config.js';

const ROUTE_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// Add tomtomKey to both local_config and production_config in app/config.js to enable traffic tiles
// Free tier: https://developer.tomtom.com/  (2500 tile requests/day)
const TRAFFIC_TILE_URL = () => {
    const key = config.tomtomKey;
    return key
        ? `https://api.tomtom.com/traffic/map/4/tile/flow/relative/{z}/{x}/{y}.png?key=${key}`
        : null;
};

const MAP_STYLES = {
    streets:   'https://demotiles.maplibre.org/style.json',
    satellite: { version: 8, sources: { sat: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: 'Esri World Imagery' } }, layers: [{ id: 'sat', type: 'raster', source: 'sat' }] },
    terrain:   { version: 8, sources: { topo: { type: 'raster', tiles: ['https://a.tile.opentopomap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenTopoMap' } }, layers: [{ id: 'topo', type: 'raster', source: 'topo' }] },
    dark:      { version: 8, sources: { osm: { type: 'raster', tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap' } }, layers: [{ id: 'osm', type: 'raster', source: 'osm' }], glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf' },
};

export default class LiveMapPage extends Page {
    constructor(options = {}) {
        super({
            title: 'Live Map',
            icon: 'bi-map',
            pageName: 'group/live/map',
            route: 'group/live/map',
            permissions: ['routes', 'view_routes', 'manage_routes', 'manage_group'],
            requiresGroup: true,
            ...options,
        });

        this.mapView = null;
        this.mapReady = false;
        this.mapError = null;
        this.mapStyle = 'dark';
        this.trafficOn = false;
        this.routes = [];
        this.visibleRouteIds = new Set();
        this._positionHandler = this._onOperatorPosition.bind(this);
    }

    async onInit() {
        app.events.on('route:operator_position', this._positionHandler);
        app.events.on('route:stop_update', this._positionHandler);
        await this.loadRoutes();
        await this._initMap();
    }

    async onAfterRender() {
        await super.onAfterRender();
        this._drawRouteLines();
    }

    async onEnter() {
        await super.onEnter();
        if (this.mapView) {
            await this.loadRoutes();
            const markers = this._buildMarkers();
            this.mapView.updateMarkers(markers);
            if (markers.length > 0) this.mapView.fitBounds();
            this._drawRouteLines();
            this._renderPills();
            this._renderBadges();
        }
    }

    onDestroy() {
        app.events.off('route:operator_position', this._positionHandler);
        app.events.off('route:stop_update', this._positionHandler);
        if (this.mapView?.destroy) this.mapView.destroy();
    }

    async loadRoutes() {
        const today = new Date().toISOString().slice(0, 10);
        const listResponse = await app.api.get('/api/routes/route', {
            group: app.activeGroup.id,
            scheduled_date: today,
            size: 100,
        });

        if (!listResponse.success || !listResponse.data.status) return;

        const routes = (listResponse.data.data || []).filter(r =>
            ['scheduled', 'in_progress'].includes(r.status)
        );

        const detailed = await Promise.all(routes.map(async r => {
            const res = await app.api.get(`/api/routes/route/${r.id}`, { graph: 'detail' });
            const route = (res.success && res.data.status) ? res.data.data : r;

            if (!route.stops_list?.length) {
                const stopsRes = await app.api.get('/api/routes/stop', {
                    route: r.id,
                    size: 200,
                    sort: 'sequence_position',
                });
                if (stopsRes.success && stopsRes.data.status) {
                    route.stops_list = stopsRes.data.data || [];
                }
            }

            return route;
        }));

        this.routes = detailed;

        // Initialise visibility — new routes default to visible
        for (const route of this.routes) {
            if (!this.visibleRouteIds.has(route.id)) {
                this.visibleRouteIds.add(route.id);
            }
        }
        // Remove stale route ids that no longer exist
        const currentIds = new Set(this.routes.map(r => r.id));
        for (const id of this.visibleRouteIds) {
            if (!currentIds.has(id)) this.visibleRouteIds.delete(id);
        }
    }

    async _initMap() {
        try {
            const { MapLibreView } = await import('web-mojo/map');
            const markers = this._buildMarkers();

            let center = null;
            if (markers.length === 0) {
                for (const route of this.routes) {
                    const stop = (route.stops_list || []).find(s =>
                        (s.location?.longitude || s.longitude) &&
                        (s.location?.latitude || s.latitude)
                    );
                    if (stop) {
                        center = [
                            stop.location?.longitude || stop.longitude,
                            stop.location?.latitude  || stop.latitude,
                        ];
                        break;
                    }
                }
            }

            this.mapView = new MapLibreView({
                containerId: 'map-container',
                markers,
                zoom: markers.length > 0 ? 12 : 10,
                ...(center ? { center } : {}),
                height: Math.max(500, window.innerHeight - 200),
                style: this.mapStyle,
            });
            this.addChild(this.mapView);
            this.mapReady = true;
        } catch (e) {
            console.warn('LiveMapPage: map not available.', e);
            this.mapError = 'Map component could not be loaded.';
        }
    }

    _routeColor(routeId) {
        const idx = this.routes.findIndex(r => r.id === routeId);
        return ROUTE_COLORS[idx % ROUTE_COLORS.length] || '#3b82f6';
    }

    _drawRouteLines(retries = 0) {
        if (!this.mapView) return;
        if (!this.mapView.map) {
            // MapLibre GL loads async from CDN — retry until the instance exists
            if (retries < 30) setTimeout(() => this._drawRouteLines(retries + 1), 200);
            return;
        }
        const map = this.mapView.map;

        const doDrawing = () => {
            for (let i = 0; i < this.routes.length; i++) {
                const route = this.routes[i];
                const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
                const sourceId = `route-line-${route.id}`;
                const layerId = `route-line-layer-${route.id}`;
                const visible = this.visibleRouteIds.has(route.id);

                const coords = (route.stops_list || [])
                    .map(s => {
                        const lng = s.location?.longitude || s.longitude;
                        const lat = s.location?.latitude  || s.latitude;
                        return (lng && lat) ? [lng, lat] : null;
                    })
                    .filter(Boolean);

                const geojson = {
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: coords },
                };

                if (map.getSource(sourceId)) {
                    map.getSource(sourceId).setData(geojson);
                } else {
                    map.addSource(sourceId, { type: 'geojson', data: geojson });
                    map.addLayer({
                        id: layerId,
                        type: 'line',
                        source: sourceId,
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                            visibility: visible ? 'visible' : 'none',
                        },
                        paint: {
                            'line-color': color,
                            'line-width': 3,
                            'line-dasharray': [2, 1],
                        },
                    });
                }

                if (map.getLayer(layerId)) {
                    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
                }
            }
        };

        if (map.loaded()) {
            doDrawing();
        } else {
            map.once('load', doDrawing);
        }
    }

    _applyRouteLines() {
        if (!this.mapView?.map) return;
        const map = this.mapView.map;
        for (let i = 0; i < this.routes.length; i++) {
            const route = this.routes[i];
            const layerId = `route-line-layer-${route.id}`;
            if (map.getLayer(layerId)) {
                map.setLayoutProperty(
                    layerId, 'visibility',
                    this.visibleRouteIds.has(route.id) ? 'visible' : 'none'
                );
            }
        }
    }

    _buildMarkers() {
        const markers = [];

        for (let i = 0; i < this.routes.length; i++) {
            const route = this.routes[i];
            if (!this.visibleRouteIds.has(route.id)) continue;

            const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
            const op = route.operator;
            const stops = route.stops_list || [];

            if (op?.current_latitude && op?.current_longitude) {
                markers.push({
                    lat: op.current_latitude,
                    lng: op.current_longitude,
                    popup: `<strong>${op.display_name || 'Operator'}</strong><br>${route.template?.name || 'Route'}`,
                    color,
                });
            }

            for (const stop of stops) {
                const lat = stop.location?.latitude || stop.latitude;
                const lng = stop.location?.longitude || stop.longitude;
                if (!lat || !lng) continue;

                const stopColor = {
                    completed:   '#198754',
                    in_progress: '#ffc107',
                    arrived:     '#0dcaf0',
                    skipped:     '#6c757d',
                    pending:     '#adb5bd',
                }[stop.status] || '#adb5bd';

                markers.push({
                    lat,
                    lng,
                    popup: `<strong>${stop.label || stop.location?.name || 'Stop'}</strong><br>${stop.activity_type || ''}<br><em>${stop.status}</em>`,
                    color: stopColor,
                });
            }
        }

        return markers;
    }

    async _onOperatorPosition() {
        await this.loadRoutes();
        if (this.mapView?.updateMarkers) {
            this.mapView.updateMarkers(this._buildMarkers());
        } else if (this.mapView?.addMarkers) {
            this.mapView.clearMarkers?.();
            this.mapView.addMarkers(this._buildMarkers());
        }
        this._applyRouteLines();
        this._renderPills();
        this._renderBadges();
    }

    onActionRefresh() {
        this._onOperatorPosition();
    }

    onActionSetStyle(event, el) {
        const style = el.getAttribute('data-style');
        if (!style || style === this.mapStyle || !this.mapView?.map) return;

        this.mapStyle = style;
        const map = this.mapView.map;

        // Remove existing route layers/sources before style wipes them
        for (const route of this.routes) {
            const layerId = `route-line-layer-${route.id}`;
            const sourceId = `route-line-${route.id}`;
            if (map.getLayer(layerId)) map.removeLayer(layerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);
        }

        map.setStyle(MAP_STYLES[style]);
        map.once('style.load', () => {
            this._drawRouteLines();
            if (this.trafficOn) {
                const trafficUrl = TRAFFIC_TILE_URL();
                if (trafficUrl) {
                    map.addSource('traffic', { type: 'raster', tiles: [trafficUrl], tileSize: 256, attribution: '© TomTom' });
                    map.addLayer({ id: 'traffic-layer', type: 'raster', source: 'traffic', paint: { 'raster-opacity': 0.7 } });
                }
            }
        });

        this._renderStyleButtons();
    }

    _renderStyleButtons() {
        const el = this.el?.querySelector('[data-container="style-buttons"]');
        if (el) el.innerHTML = this._styleButtonsHtml();
    }

    _styleButtonsHtml() {
        const styles = [
            { key: 'streets',   label: 'Streets',   icon: 'bi-map' },
            { key: 'satellite', label: 'Satellite',  icon: 'bi-globe' },
            { key: 'terrain',   label: 'Terrain',    icon: 'bi-geo' },
            { key: 'dark',      label: 'Dark',       icon: 'bi-moon' },
        ];
        return styles.map(s => `
            <button type="button"
                class="btn btn-sm ${this.mapStyle === s.key ? 'btn-secondary' : 'btn-outline-secondary'}"
                data-action="set-style" data-style="${s.key}"
                title="${s.label}">
                <i class="bi ${s.icon} me-1"></i>${s.label}
            </button>
        `).join('');
    }

    onActionToggleTraffic() {
        const trafficUrl = TRAFFIC_TILE_URL();
        if (!trafficUrl) {
            app.toast.warning('Traffic tiles require a TomTom API key (config.tomtomKey)');
            return;
        }
        this.trafficOn = !this.trafficOn;
        const map = this.mapView?.map;
        if (!map) return;

        if (this.trafficOn) {
            if (!map.getSource('traffic')) {
                map.addSource('traffic', { type: 'raster', tiles: [trafficUrl], tileSize: 256, attribution: '© TomTom' });
                map.addLayer({ id: 'traffic-layer', type: 'raster', source: 'traffic', paint: { 'raster-opacity': 0.7 } });
            }
        } else {
            if (map.getLayer('traffic-layer')) map.removeLayer('traffic-layer');
            if (map.getSource('traffic')) map.removeSource('traffic');
        }
        this._renderTrafficButton();
    }

    _renderTrafficButton() {
        const el = this.el?.querySelector('[data-container="traffic-button"]');
        if (el) el.innerHTML = this._trafficButtonHtml();
    }

    _trafficButtonHtml() {
        const hasKey = !!TRAFFIC_TILE_URL();
        return `
            <button type="button"
                class="btn btn-sm ${this.trafficOn ? 'btn-warning' : 'btn-outline-secondary'}"
                data-action="toggle-traffic"
                title="${hasKey ? 'Toggle traffic overlay' : 'Requires TomTom API key'}">
                <i class="bi bi-car-front me-1"></i>Traffic
            </button>
        `;
    }

    onActionBack() {
        app.sidebar.setActiveMenu('group_routes');
        app.navigate('?page=group/live');
    }

    onActionToggleRoute(event, el) {
        const routeId = parseInt(el.getAttribute('data-route-id'));
        if (this.visibleRouteIds.has(routeId)) {
            this.visibleRouteIds.delete(routeId);
        } else {
            this.visibleRouteIds.add(routeId);
        }

        // Update markers and lines without destroying the map
        if (this.mapView?.updateMarkers) {
            this.mapView.updateMarkers(this._buildMarkers());
        }
        this._applyRouteLines();
        this._renderPills();
    }

    /** Surgical update — replaces only the pills container */
    _renderPills() {
        const container = this.el?.querySelector('[data-container="route-pills"]');
        if (container) container.innerHTML = this._pillsHtml();
    }

    /** Surgical update — replaces only the badge row */
    _renderBadges() {
        const container = this.el?.querySelector('[data-container="map-badges"]');
        if (container) container.innerHTML = this._badgesHtml();
    }

    _pillsHtml() {
        if (this.routes.length === 0) return '';
        return this.routes.map((route, i) => {
            const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
            const active = this.visibleRouteIds.has(route.id);
            const name = route.template?.name || `Route ${route.id}`;
            const opName = route.operator?.display_name || route.operator?.user?.username || 'Unassigned';
            return `
                <button type="button"
                    class="btn btn-sm ${active ? '' : 'opacity-50'}"
                    style="border: 2px solid ${color}; background: ${active ? color + '22' : 'transparent'}; color: inherit;"
                    data-action="toggle-route"
                    data-route-id="${route.id}"
                    title="${active ? 'Hide' : 'Show'} route">
                    <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:4px;"></span>
                    ${name}
                    <span class="text-muted small ms-1">${opName}</span>
                </button>
            `;
        }).join('');
    }

    _badgesHtml() {
        const inProgressCount = this.routes.filter(r => r.status === 'in_progress').length;
        const totalCount = this.routes.length;
        return `
            ${inProgressCount > 0
                ? `<span class="badge bg-primary">${inProgressCount} in progress</span>`
                : ''}
            ${totalCount > 0
                ? `<span class="badge bg-secondary">${totalCount} route${totalCount !== 1 ? 's' : ''} today</span>`
                : `<span class="badge bg-secondary">No routes today</span>`}
        `;
    }

    set template(v) { this._t = v; }

    get template() {
        return `
            <div class="p-3">
                <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                    <div class="d-flex align-items-center gap-2">
                        <button type="button" class="btn btn-sm btn-outline-secondary" data-action="back">
                            <i class="bi bi-arrow-left"></i>
                        </button>
                        <span class="fw-semibold">Live Map</span>
                        <span data-container="map-badges">${this._badgesHtml()}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <div class="btn-group" data-container="style-buttons">
                            ${this._styleButtonsHtml()}
                        </div>
                        <span data-container="traffic-button">${this._trafficButtonHtml()}</span>
                        <button type="button" class="btn btn-sm btn-outline-secondary" data-action="refresh">
                            <i class="bi bi-arrow-clockwise me-1"></i>Refresh
                        </button>
                    </div>
                </div>

                ${this.routes.length > 1 ? `
                    <div class="d-flex flex-wrap gap-2 mb-2" data-container="route-pills">
                        ${this._pillsHtml()}
                    </div>
                ` : `<div data-container="route-pills" class="d-flex flex-wrap gap-2 mb-2"></div>`}

                ${this.mapError ? `
                    <div class="alert alert-warning">
                        <i class="bi bi-exclamation-triangle me-1"></i>${this.mapError}
                    </div>
                ` : ''}

                <div data-container="map-container" style="border-radius:8px; overflow:hidden;"></div>

                <div class="d-flex gap-3 mt-2 small text-muted flex-wrap">
                    <span><span class="badge" style="background:#198754;">&nbsp;</span> Completed</span>
                    <span><span class="badge" style="background:#ffc107;">&nbsp;</span> In Progress</span>
                    <span><span class="badge" style="background:#0dcaf0;">&nbsp;</span> Arrived</span>
                    <span><span class="badge" style="background:#adb5bd;">&nbsp;</span> Pending</span>
                    <span><span class="badge" style="background:#6c757d;">&nbsp;</span> Skipped</span>
                    <span class="ms-3 text-muted">&#9679; Operator position uses route color</span>
                </div>
            </div>
        `;
    }
}
